import React, { useState } from 'react';

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: {
    emailConfirmacaoHtml: string;
    emailCancelamentoHtml: string;
    calendarTituloTemplate: string;
    calendarDescricaoTemplate: string;
    calendarLocalizacaoTemplate: string;
  };
}

interface DadosExemplo {
  paciente_nome: string;
  data_consulta: string;
  hora_consulta: string;
  hora_fim: string;
  psicologo_nome: string;
  valor_consulta: string;
  observacoes: string;
  link_consulta_online: string;
  observacoes_bloco: string;
  link_consulta_bloco: string;
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  isOpen,
  onClose,
  templates
}) => {
  const [tipoVisualizacao, setTipoVisualizacao] = useState<'email-confirmacao' | 'email-cancelamento' | 'calendar'>('email-confirmacao');

  // Dados de exemplo para preview
  const dadosExemplo: DadosExemplo = {
    paciente_nome: 'Maria Silva',
    data_consulta: '15/08/2025',
    hora_consulta: '14:30',
    hora_fim: '15:30',
    psicologo_nome: 'Dr. João Santos',
    valor_consulta: '150,00',
    observacoes: 'Primeira consulta - ansiedade',
    link_consulta_online: 'https://meet.google.com/abc-defg-hij',
    observacoes_bloco: 'Primeira consulta - ansiedade',
    link_consulta_bloco: 'Link: https://meet.google.com/abc-defg-hij'
  };

  // Função para processar template substituindo variáveis
  const processarTemplate = (template: string, dados: DadosExemplo): string => {
    if (!template) return '';
    
    let resultado = template;
    
    // Substituir todas as variáveis
    Object.entries(dados).forEach(([chave, valor]) => {
      const regex = new RegExp(`{{${chave}}}`, 'g');
      resultado = resultado.replace(regex, valor || '');
    });
    
    return resultado;
  };

  // Renderizar preview baseado no tipo
  const renderizarPreview = () => {
    switch (tipoVisualizacao) {
      case 'email-confirmacao':
        const emailConfirmacao = processarTemplate(templates.emailConfirmacaoHtml, dadosExemplo);
        return (
          <div className="border rounded p-3" style={{ minHeight: '300px' }}>
            <h6 className="text-muted mb-3">
              <i className="fas fa-envelope mr-2"></i>
              Preview - Email de Confirmação
            </h6>
            {emailConfirmacao ? (
              <div dangerouslySetInnerHTML={{ __html: emailConfirmacao }} />
            ) : (
              <div className="text-muted font-italic">
                Nenhum template personalizado configurado. Será usado o template padrão do sistema.
              </div>
            )}
          </div>
        );
        
      case 'email-cancelamento':
        const emailCancelamento = processarTemplate(templates.emailCancelamentoHtml, dadosExemplo);
        return (
          <div className="border rounded p-3" style={{ minHeight: '300px' }}>
            <h6 className="text-muted mb-3">
              <i className="fas fa-envelope mr-2"></i>
              Preview - Email de Cancelamento
            </h6>
            {emailCancelamento ? (
              <div dangerouslySetInnerHTML={{ __html: emailCancelamento }} />
            ) : (
              <div className="text-muted font-italic">
                Nenhum template personalizado configurado. Será usado o template padrão do sistema.
              </div>
            )}
          </div>
        );
        
      case 'calendar':
        const tituloCalendar = processarTemplate(templates.calendarTituloTemplate, dadosExemplo);
        const descricaoCalendar = processarTemplate(templates.calendarDescricaoTemplate, dadosExemplo);
        const localizacaoCalendar = processarTemplate(templates.calendarLocalizacaoTemplate, dadosExemplo);
        
        return (
          <div className="border rounded p-3" style={{ minHeight: '300px' }}>
            <h6 className="text-muted mb-3">
              <i className="fas fa-calendar mr-2"></i>
              Preview - Evento Google Calendar
            </h6>
            
            <div className="card mb-3">
              <div className="card-body">
                <h5 className="card-title text-primary">
                  {tituloCalendar || 'Consulta - Maria Silva'}
                </h5>
                
                <div className="row mb-2">
                  <div className="col-sm-3"><strong>Data:</strong></div>
                  <div className="col-sm-9">{dadosExemplo.data_consulta}</div>
                </div>
                
                <div className="row mb-2">
                  <div className="col-sm-3"><strong>Horário:</strong></div>
                  <div className="col-sm-9">{dadosExemplo.hora_consulta} - {dadosExemplo.hora_fim}</div>
                </div>
                
                {localizacaoCalendar && (
                  <div className="row mb-2">
                    <div className="col-sm-3"><strong>Local:</strong></div>
                    <div className="col-sm-9">{localizacaoCalendar}</div>
                  </div>
                )}
                
                {descricaoCalendar && (
                  <div className="row">
                    <div className="col-sm-3"><strong>Descrição:</strong></div>
                    <div className="col-sm-9">
                      <div className="bg-light p-2 rounded">
                        <div dangerouslySetInnerHTML={{ __html: descricaoCalendar.replace(/\n/g, '<br>') }} />
                      </div>
                    </div>
                  </div>
                )}
                
                {(!descricaoCalendar || descricaoCalendar.trim() === '') && (
                  <div className="text-muted font-italic">
                    Nenhuma descrição personalizada configurada. Será usada a descrição padrão do sistema.
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal fade show d-block" 
      style={{ 
        backgroundColor: 'rgba(0,0,0,0.5)', 
        zIndex: 1050,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'auto'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="modal-dialog modal-xl modal-dialog-scrollable" 
        style={{ 
          margin: '1rem auto',
          maxWidth: '90vw',
          height: 'calc(100vh - 2rem)'
        }}
      >
        <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header flex-shrink-0">
            <h5 className="modal-title">
              <i className="fas fa-eye mr-2"></i>
              Preview dos Templates de Notificação
            </h5>
            <button 
              type="button" 
              className="close" 
              onClick={onClose}
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: '1.5rem', 
                fontWeight: 'bold',
                color: '#000',
                opacity: 0.5
              }}
            >
              <span>&times;</span>
            </button>
          </div>
          
          <div className="modal-body flex-grow-1" style={{ overflow: 'auto', padding: '1rem' }}>
            {/* Dados de exemplo usados */}
            <div className="alert alert-info mb-4">
              <h6><i className="fas fa-info-circle mr-1"></i>{' '}Dados de Exemplo Utilizados:</h6>
              <div className="row small">
                <div className="col-md-6">
                  <ul className="mb-0">
                    <li><strong>Paciente:</strong> {dadosExemplo.paciente_nome}</li>
                    <li><strong>Data:</strong> {dadosExemplo.data_consulta}</li>
                    <li><strong>Horário:</strong> {dadosExemplo.hora_consulta} - {dadosExemplo.hora_fim}</li>
                    <li><strong>Psicólogo:</strong> {dadosExemplo.psicologo_nome}</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="mb-0">
                    <li><strong>Valor:</strong> R$ {dadosExemplo.valor_consulta}</li>
                    <li><strong>Observações:</strong> {dadosExemplo.observacoes}</li>
                    <li><strong>Link da Consulta:</strong> <span className="text-truncate d-inline-block" style={{maxWidth: '200px'}}>{dadosExemplo.link_consulta_online}</span></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Seletor de tipo de preview */}
            <div className="mb-4">
              <div className="form-label font-weight-bold">Tipo de Notificação:</div>
              <div className="btn-group d-flex" role="group" style={{ flexWrap: 'wrap' }}>
                <input
                  type="radio"
                  className="btn-check d-none"
                  name="tipoPreview"
                  id="email-confirmacao"
                  value="email-confirmacao"
                  checked={tipoVisualizacao === 'email-confirmacao'}
                  onChange={(e) => setTipoVisualizacao(e.target.value as any)}
                />
                <label 
                  className={`btn ${tipoVisualizacao === 'email-confirmacao' ? 'btn-primary' : 'btn-outline-primary'} flex-fill`} 
                  htmlFor="email-confirmacao"
                  style={{ minWidth: '150px', margin: '2px' }}
                >
                  <i className="fas fa-envelope mr-2"></i>
                  Email Confirmação
                </label>

                <input
                  type="radio"
                  className="btn-check d-none"
                  name="tipoPreview"
                  id="email-cancelamento"
                  value="email-cancelamento"
                  checked={tipoVisualizacao === 'email-cancelamento'}
                  onChange={(e) => setTipoVisualizacao(e.target.value as any)}
                />
                <label 
                  className={`btn ${tipoVisualizacao === 'email-cancelamento' ? 'btn-danger' : 'btn-outline-danger'} flex-fill`} 
                  htmlFor="email-cancelamento"
                  style={{ minWidth: '150px', margin: '2px' }}
                >
                  <i className="fas fa-envelope mr-2"></i>
                  Email Cancelamento
                </label>

                <input
                  type="radio"
                  className="btn-check d-none"
                  name="tipoPreview"
                  id="calendar"
                  value="calendar"
                  checked={tipoVisualizacao === 'calendar'}
                  onChange={(e) => setTipoVisualizacao(e.target.value as any)}
                />
                <label 
                  className={`btn ${tipoVisualizacao === 'calendar' ? 'btn-success' : 'btn-outline-success'} flex-fill`} 
                  htmlFor="calendar"
                  style={{ minWidth: '150px', margin: '2px' }}
                >
                  <i className="fas fa-calendar mr-2"></i>
                  Google Calendar
                </label>
              </div>
            </div>

            {/* Área de preview com scroll */}
            <div style={{ maxHeight: '400px', overflow: 'auto', marginBottom: '1rem' }}>
              {renderizarPreview()}
            </div>

            {/* Variáveis disponíveis */}
            <div className="mt-4">
              <h6 className="font-weight-bold mb-3">
                <i className="fas fa-code mr-2"></i>
                Variáveis Disponíveis nos Templates:
              </h6>
              <div className="row">
                <div className="col-lg-6 col-md-12 mb-3">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title">Dados do Paciente:</h6>
                      <ul className="list-unstyled small">
                        <li><code>{'{{paciente_nome}}'}</code> - Nome do paciente</li>
                      </ul>
                      
                      <h6 className="card-title mt-3">Data e Hora:</h6>
                      <ul className="list-unstyled small">
                        <li><code>{'{{data_consulta}}'}</code> - Data da consulta</li>
                        <li><code>{'{{hora_consulta}}'}</code> - Hora de início</li>
                        <li><code>{'{{hora_fim}}'}</code> - Hora de término</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-12 mb-3">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title">Outros Dados:</h6>
                      <ul className="list-unstyled small">
                        <li><code>{'{{psicologo_nome}}'}</code> - Nome do psicólogo</li>
                        <li><code>{'{{valor_consulta}}'}</code> - Valor da consulta</li>
                        <li><code>{'{{observacoes}}'}</code> - Observações da consulta</li>
                        <li><code>{'{{link_consulta_online}}'}</code> - Link da consulta</li>
                        <li><code>{'{{observacoes_bloco}}'}</code> - Observações (se houver)</li>
                        <li><code>{'{{link_consulta_bloco}}'}</code> - Link formatado</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-footer flex-shrink-0">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times mr-2"></i>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
